<template>
  <div class="widget">
    <div class="header">
      <span class="bmi-label">ИМТ</span>
      <div class="lock-icon" :style="{ background: iconColor }">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20px"
          height="20px"
          viewBox="0 0 24 24"
          fill="none"
        >
          <path
            d="M12.0002 6L11.0064 9M16.5 6C17.8978 6 18.5967 6 19й.1481 6.22836C19.8831 6.53284 20.4672 7.11687 20.7716 7.85195C21 8.40326 21 9.10218 21 10.5V16.2C21 17.8802 21 18.7202 20.673 19.362C20.3854 19.9265 19.9265 20.3854 19.362 20.673C18.7202 21 17.8802 21 16.2 21H7.8C6.11984 21 5.27976 21 4.63803 20.673C4.07354 20.3854 3.6146 19.9265 3.32698 19.362C3 18.7202 3 17.8802 3 16.2V10.5C3 9.10218 3 8.40326 3.22836 7.85195C3.53284 7.11687 4.11687 6.53284 4.85195 6.22836C5.40326 6 6.10218 6 7.5 6M10 17H14M10.5415 3H13.4588C14.5397 3 15.0802 3 15.4802 3.18541C16.0136 3.43262 16.4112 3.90199 16.5674 4.46878C16.6845 4.89387 16.5957 5.42698 16.418 6.4932C16.2862 7.28376 16.2203 7.67904 16.0449 7.98778C15.8111 8.39944 15.4388 8.71481 14.9943 8.87778C14.661 9 14.2602 9 13.4588 9H10.5415C9.74006 9 9.33933 9 9.00596 8.87778C8.56146 8.71481 8.18918 8.39944 7.95536 7.98778C7.77999 7.67904 7.71411 7.28376 7.58235 6.4932C7.40465 5.42698 7.3158 4.89387 7.43291 4.46878C7.58906 3.90199 7.98669 3.43262 8.52009 3.18541C8.92014 3 9.46061 3 10.5415 3Z"
            stroke="#fff9fae8"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </div>
    </div>

    <div class="bmi-value">{{ currentBmi }}</div>

    <div class="indicator">
      <div class="indicator-segment segment-1"></div>
      <div class="indicator-segment segment-2"></div>
      <div class="indicator-segment segment-3"></div>
      <div class="indicator-segment segment-4"></div>
      <div class="indicator-segment segment-5"></div>
      <div class="indicator-segment segment-6"></div>
    </div>

    <div class="slider">
      <div class="slider-thumb" :style="{ left: position }"></div>
    </div>

    <div class="status">{{ bmiStatus }}</div>
    <div class="range">
      {{ bmiRange }}
      <span class="who-label"
        >{{ currentWeight }} кг
        <span
          :style="{
            color: weightDynamics > 0 ? '#dc3545' : '#20c997',
          }"
          v-if="previousWeight"
        >
          {{ weightDynamics > 0 ? "▲" : "▼" }}
        </span>
      </span>
    </div>
  </div>
</template>

<script lang="ts">
import { ref, onMounted, onUnmounted, computed } from "vue";
import { defineComponent } from "vue";
import { fetchWeight } from "@/services/api";

export default defineComponent({
  name: "VesDynWidget",
  setup() {
    const weightData = ref<any[]>([]);
    const currentWeight = ref(0);
    const previousWeight = ref<number | null>(null);
    const currentBmi = ref(0);

    const loadWeightData = async () => {
      try {
        const data = await fetchWeight();
        if (data && data.length > 0) {
          weightData.value = data.sort(
            (a: any, b: any) =>
              new Date(b.created_at).getTime() -
              new Date(a.created_at).getTime()
          );

          // Получаем данные из API или локального хранилища
          const localWeightData = JSON.parse(
            localStorage.getItem("weightData") || "[]"
          );

          if (weightData.value.length > 0) {
            currentWeight.value = weightData.value[0].current_weight;
            currentBmi.value = weightData.value[0].bmi;
            if (weightData.value.length > 1) {
              previousWeight.value = weightData.value[1].current_weight;
            }
          } else if (localWeightData.length > 0) {
            // Используем данные из локального хранилища, если API недоступен
            currentWeight.value = localWeightData[0].current_weight;
            currentBmi.value = localWeightData[0].bmi;
            if (localWeightData.length > 1) {
              previousWeight.value = localWeightData[1].current_weight;
            }
          }

          // Сохраняем новые данные в локальное хранилище
          if (weightData.value.length > 0) {
            localStorage.setItem(
              "weightData",
              JSON.stringify(weightData.value)
            );
          }
        }
      } catch (error) {
        console.error("Ошибка при загрузке данных веса:", error);
        // При ошибке API пытаемся загрузить данные из локального хранилища
        const localWeightData = JSON.parse(
          localStorage.getItem("weightData") || "[]"
        );
        if (localWeightData.length > 0) {
          currentWeight.value = localWeightData[0].current_weight;
          currentBmi.value = localWeightData[0].bmi;
          if (localWeightData.length > 1) {
            previousWeight.value = localWeightData[1].current_weight;
          }
        }
      }
    };

    let intervalId: number;

    onMounted(() => {
      loadWeightData();
      intervalId = window.setInterval(loadWeightData, 5000);
    });

    onUnmounted(() => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    });

    const weightDynamics = computed(() => {
      if (!previousWeight.value) return 0;
      return currentWeight.value - previousWeight.value;
    });

    const position = computed(() => {
      const bmi = currentBmi.value;
      if (bmi < 18.5) return "10%";
      if (bmi < 25) return "30%";
      if (bmi < 30) return "50%";
      if (bmi < 35) return "70%";
      return "90%";
    });

    const bmiStatus = computed(() => {
      const bmi = currentBmi.value;
      if (bmi < 18.5) return "Дефицит";
      if (bmi < 25) return "Норма";
      if (bmi < 30) return "Избыточная";
      if (bmi < 35) return "Ожирение I ст";
      if (bmi < 40) return "Ожирение II ст";
      return "Ожирение III ст";
    });

    const bmiRange = computed(() => {
      const bmi = currentBmi.value;
      if (bmi < 18.5) return "< 18,5";
      if (bmi < 25) return "18,5 – 24,9";
      if (bmi < 30) return "25,0 – 29,9";
      if (bmi < 35) return "30,0 – 34,9";
      if (bmi < 35) return "35,0 – 39,9";
      return "> 40";
    });

    const iconColor = computed(() => {
      const bmi = currentBmi.value;
      if (bmi < 18.5) return "#"; // зеленый
      if (bmi < 20) return "#4cd964"; // светло-зеленый
      if (bmi < 25) return "#a4d93c"; // желтый
      if (bmi < 27.5) return "#ffcc00"; // светло-оранжевый
      if (bmi < 30) return "#ff9500"; // оранжевый
      if (bmi < 35) return "#ff5733"; // темно-оранжевый
      if (bmi < 40) return "#ff3b30"; // красный
      return "#ff0000"; // темно-красный
    });

    return {
      currentWeight,
      previousWeight,
      currentBmi,
      weightDynamics,
      position,
      bmiStatus,
      bmiRange,
      iconColor,
    };
  },
});
</script>

<style lang="scss" scoped>
.widget {
  width: 100%;
  padding: 16px;
  border-radius: 24px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.bmi-label {
  color: #2c3d50;
  font-size: 14px;
  font-weight: 500;
}

.lock-icon {
  background: #f59e0b;
  padding: 8px;
  border-radius: 50%;
  height: 30px;
  width: 30px;
  display: flex;
  align-content: center;
  justify-content: center;
  align-items: center;
  box-shadow: 1px 1px 16px 0px #dddddd;
}

.bmi-value {
  color: #2c3d50;
  font-size: 32px;
  font-weight: 600;
}

.indicator {
  display: flex;
  gap: 1px;
  margin-bottom: 8px;
}

.indicator-segment {
  height: 8px;
  flex: 1;
  border-radius: 4px;
}

.segment-1 {
  background: #4cd964;
}
.segment-2 {
  background: #a4d93c;
}
.segment-3 {
  background: #ffd60a;
}
.segment-4 {
  background: #ff9500;
}
.segment-5 {
  background: #ff3b30;
}
.segment-6 {
  background: #ff453a;
}

.slider {
  position: relative;
  height: 4px;
  margin: 8px 0 12px;
}

.slider-thumb {
  position: absolute;
  width: 4px;
  height: 12px;
  background: rgb(0, 0, 0);
  border-radius: 2px;
  left: 60%;
  top: -4px;
  transform: translateX(-50%);
}

.status {
  color: rgb(62, 62, 62);
  font-size: 10pt;
}

.range {
  color: #71717a;
  font-size: 14px;
  display: flex;
  align-items: center;
}

.who-label {
  margin-left: 8px;
  background: #2c3d50;
  padding: 4px 8px;
  border-radius: 8px;
  font-size: 12px;
  color: #ffffff;
}
</style>
